"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useAppContext } from "@/contexts/app";
import { isAuthEnabled } from "@/lib/auth";
import { useRouter } from "@/i18n/navigation";
import { trackEvent } from "@/lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, Check, Zap, Loader2 } from "lucide-react";

const MODELS = [
  {
    id: "claude-opus-4-6",
    name: "Claude Opus 4.6",
    subtitle: "Available",
    icon: "https://upload.wikimedia.org/wikipedia/commons/b/b0/Claude_AI_symbol.svg",
    available: true,
  },
  {
    id: "gpt-5-2",
    name: "GPT-5.2",
    subtitle: "Available",
    icon: "https://img.icons8.com/androidL/512/FFFFFF/chatgpt.png",
    available: true,
  },
  {
    id: "gemini-3-pro",
    name: "Gemini 3 Pro",
    subtitle: "Available",
    icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1d/Google_Gemini_icon_2025.svg/960px-Google_Gemini_icon_2025.svg.png",
    available: true,
  },
];

const CHANNELS = [
  {
    id: "telegram",
    name: "Telegram",
    subtitle: "Available",
    icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/82/Telegram_logo.svg/960px-Telegram_logo.svg.png",
    available: true,
  },
  {
    id: "discord",
    name: "Discord",
    subtitle: "Available",
    icon: "https://scbwi-storage-prod.s3.amazonaws.com/images/discord-mark-blue_rA6tXJo.png",
    available: true,
  },
  {
    id: "whatsapp",
    name: "WhatsApp",
    subtitle: "Available",
    icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6b/WhatsApp.svg/960px-WhatsApp.svg.png",
    available: true,
  },
];

const MODEL_CANONICAL_MAP: Record<string, string> = {
  "claude-opus-4-6": "openrouter/anthropic/claude-opus-4",
  "openrouter/anthropic/claude-opus-4-6": "openrouter/anthropic/claude-opus-4",
  "openrouter/anthropic/claude-opus-4": "openrouter/anthropic/claude-opus-4",
  "gpt-5-2": "openrouter/openai/gpt-5.2",
  "gpt-5.2": "openrouter/openai/gpt-5.2",
  "openrouter/openai/gpt-5.2": "openrouter/openai/gpt-5.2",
  "gemini-3-pro": "openrouter/google/gemini-3-pro-preview",
  "openrouter/google/gemini-3-pro": "openrouter/google/gemini-3-pro-preview",
  "openrouter/google/gemini-3-pro-preview":
    "openrouter/google/gemini-3-pro-preview",
};

const normalizeModelKey = (value?: string | null): string =>
  (value || "").trim().toLowerCase();

const canonicalizeModel = (value?: string | null): string => {
  const normalized = normalizeModelKey(value);
  if (!normalized) {
    return "";
  }
  return MODEL_CANONICAL_MAP[normalized] || normalized;
};

const areEquivalentModels = (
  requestedModel?: string | null,
  resolvedModel?: string | null
): boolean => {
  const requested = canonicalizeModel(requestedModel);
  const resolved = canonicalizeModel(resolvedModel);
  return !requested || !resolved || requested === resolved;
};

type DeployStatus = "idle" | "checking" | "running" | "failed";
const NO_AVAILABLE_ACCOUNT_MESSAGE = "当前订阅可用账号不足，请稍后重试";
const NETWORK_UNSTABLE_MESSAGE = "网络不稳定，请稍后再试。";
const NO_AVAILABLE_ACCOUNT_ERROR_CODE = "NO_AVAILABLE_ACCOUNT_FOR_PROVIDER";
const SUBSCRIPTION_REQUIRED_ERROR_CODE = "SUBSCRIPTION_REQUIRED";
type DeployFailureSource = "deploy_create" | "deploy_poll";
type TransientPollFailureAction = "retrying" | "failed" | "not_transient";

const TRANSIENT_NETWORK_PATTERNS = [
  /network/i,
  /fetch failed/i,
  /backend unavailable/i,
  /abort/i,
  /timeout/i,
  /econnrefused/i,
  /etimedout/i,
  /econnreset/i,
];

const parsePositiveInt = (
  raw: string | undefined,
  fallbackValue: number
): number => {
  if (!raw) {
    return fallbackValue;
  }

  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallbackValue;
  }

  return Math.floor(parsed);
};

const getPollTransientFailureMax = (): number =>
  parsePositiveInt(
    process.env.NEXT_PUBLIC_DEPLOY_POLL_TRANSIENT_MAX ||
      process.env.DEPLOY_POLL_TRANSIENT_MAX,
    3
  );

const toUserFacingDeployError = (message?: string | null): string => {
  const raw = (message || "").trim();
  if (!raw) {
    return "Deployment failed. Try again.";
  }

  if (raw.includes("NO_AVAILABLE_ACCOUNT_FOR_PROVIDER")) {
    return NO_AVAILABLE_ACCOUNT_MESSAGE;
  }

  if (TRANSIENT_NETWORK_PATTERNS.some((pattern) => pattern.test(raw))) {
    return NETWORK_UNSTABLE_MESSAGE;
  }

  return raw;
};

const normalizeDeploymentErrorCode = (
  rawCode?: string | null,
  rawMessage?: string | null
): string | null => {
  const code = (rawCode || "").trim().toUpperCase();
  if (code.includes(NO_AVAILABLE_ACCOUNT_ERROR_CODE)) {
    return NO_AVAILABLE_ACCOUNT_ERROR_CODE;
  }
  if (code.includes(SUBSCRIPTION_REQUIRED_ERROR_CODE)) {
    return SUBSCRIPTION_REQUIRED_ERROR_CODE;
  }

  const message = (rawMessage || "").trim().toUpperCase();
  if (message.includes(NO_AVAILABLE_ACCOUNT_ERROR_CODE)) {
    return NO_AVAILABLE_ACCOUNT_ERROR_CODE;
  }
  if (message.includes(SUBSCRIPTION_REQUIRED_ERROR_CODE)) {
    return SUBSCRIPTION_REQUIRED_ERROR_CODE;
  }

  return null;
};

const isTransientNetworkFailure = ({
  rawMessage,
  errorCode,
  httpStatus,
}: {
  rawMessage?: string | null;
  errorCode?: string | null;
  httpStatus?: number;
}): boolean => {
  const normalizedErrorCode = normalizeDeploymentErrorCode(errorCode, rawMessage);
  if (
    normalizedErrorCode === NO_AVAILABLE_ACCOUNT_ERROR_CODE ||
    normalizedErrorCode === SUBSCRIPTION_REQUIRED_ERROR_CODE
  ) {
    return false;
  }

  if (typeof httpStatus === "number" && httpStatus >= 500) {
    return true;
  }

  const message = (rawMessage || "").trim();
  if (!message) {
    return false;
  }

  return TRANSIENT_NETWORK_PATTERNS.some((pattern) => pattern.test(message));
};

export default function SimpleClawLanding() {
  const { user, setShowSignModal, setUser } = useAppContext();
  const router = useRouter();
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedChannel, setSelectedChannel] = useState("");
  const [token, setToken] = useState("");
  const [status, setStatus] = useState<DeployStatus>("idle");
  const [deploymentId, setDeploymentId] = useState<string | null>(null);
  const [requestedModel, setRequestedModel] = useState<string | null>(null);
  const [resolvedModel, setResolvedModel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTelegramGuide, setShowTelegramGuide] = useState(false);
  const [guideToken, setGuideToken] = useState("");
  const hasRedirectedToPricingRef = useRef(false);
  const pollTransientFailureCountRef = useRef(0);
  const maxPollTransientFailuresRef = useRef(getPollTransientFailureMax());
  const deployStartedAtRef = useRef<number | null>(null);
  const deploySuccessSentRef = useRef(false);
  const deployFailureSentRef = useRef(false);
  const deployContextRef = useRef<{
    channel: string;
    modelRequested: string | null;
    subscriptionTier: string | null;
  } | null>(null);

  const authEnabled = isAuthEnabled();
  const isSignedIn = authEnabled ? Boolean(user) : true;
  const hasModelMismatch =
    !!requestedModel &&
    !!resolvedModel &&
    !areEquivalentModels(requestedModel, resolvedModel);

  const redirectToPricing = (_source: DeployFailureSource) => {
    if (hasRedirectedToPricingRef.current) {
      return;
    }

    hasRedirectedToPricingRef.current = true;
    setStatus("idle");
    setError(null);
    setDeploymentId(null);

    router.push("/pricing?source=deploy");
  };

  const handleDeployFailure = ({
    rawMessage,
    errorCode,
    source,
  }: {
    rawMessage?: string | null;
    errorCode?: string | null;
    source: DeployFailureSource;
  }): boolean => {
    if (hasRedirectedToPricingRef.current) {
      return true;
    }

    const normalizedErrorCode = normalizeDeploymentErrorCode(errorCode, rawMessage);

    if (!deployFailureSentRef.current) {
      deployFailureSentRef.current = true;
      const durationMs =
        typeof deployStartedAtRef.current === "number"
          ? Date.now() - deployStartedAtRef.current
          : undefined;
      const ctx = deployContextRef.current;

      trackEvent("deploy_failed", {
        channel: ctx?.channel || selectedChannel || "unknown",
        model_requested:
          ctx?.modelRequested || requestedModel || selectedModel || null,
        model_resolved: resolvedModel || null,
        subscription_tier: ctx?.subscriptionTier || user?.subscriptionTier || null,
        error_code: normalizedErrorCode || errorCode || "unknown",
        source,
        duration_ms: typeof durationMs === "number" ? durationMs : undefined,
      });
    }

    if (normalizedErrorCode === NO_AVAILABLE_ACCOUNT_ERROR_CODE) {
      redirectToPricing(source);
      return true;
    }

    if (normalizedErrorCode === SUBSCRIPTION_REQUIRED_ERROR_CODE) {
      redirectToPricing(source);
      return true;
    }

    setStatus("failed");
    setError(toUserFacingDeployError(rawMessage));
    return false;
  };

  const handleTransientPollFailure = ({
    rawMessage,
    errorCode,
    httpStatus,
  }: {
    rawMessage?: string | null;
    errorCode?: string | null;
    httpStatus?: number;
  }): TransientPollFailureAction => {
    if (!isTransientNetworkFailure({ rawMessage, errorCode, httpStatus })) {
      return "not_transient";
    }

    pollTransientFailureCountRef.current += 1;
    if (pollTransientFailureCountRef.current < maxPollTransientFailuresRef.current) {
      setStatus("checking");
      setError(null);
      return "retrying";
    }

    pollTransientFailureCountRef.current = 0;
    handleDeployFailure({
      rawMessage: NETWORK_UNSTABLE_MESSAGE,
      source: "deploy_poll",
    });
    return "failed";
  };

  const ensureDeployEligibility = async (): Promise<boolean> => {
    if (!authEnabled) {
      return true;
    }

    if (!isSignedIn) {
      trackEvent("deploy_blocked", {
        reason: "not_signed_in",
        channel: selectedChannel || "unknown",
        model_requested: selectedModel || null,
        subscription_tier: user?.subscriptionTier || null,
      });
      setShowSignModal(true);
      return false;
    }

    try {
      const resp = await fetch("/api/get-user-info", { method: "POST" });
      const payload = await resp.json().catch(() => null);

      const code = payload?.code;
      const latestUser = payload?.data;

      if (!resp.ok || code !== 0 || !latestUser) {
        // Session may have expired.
        if (code === -2) {
          trackEvent("deploy_blocked", {
            reason: "session_expired",
            channel: selectedChannel || "unknown",
            model_requested: selectedModel || null,
            subscription_tier: user?.subscriptionTier || null,
          });
          setShowSignModal(true);
          return false;
        }
        // Treat unknown/non-success states as non-subscribed and guide to pricing.
        trackEvent("deploy_blocked", {
          reason: "eligibility_check_failed",
          channel: selectedChannel || "unknown",
          model_requested: selectedModel || null,
          subscription_tier: user?.subscriptionTier || null,
        });
        router.push("/pricing?source=deploy");
        return false;
      }

      setUser(latestUser);
      if (latestUser?.canDeploy === true) {
        return true;
      }

      trackEvent("deploy_blocked", {
        reason: "not_eligible",
        channel: selectedChannel || "unknown",
        model_requested: selectedModel || null,
        subscription_tier: latestUser?.subscriptionTier || user?.subscriptionTier || null,
      });
      router.push("/pricing?source=deploy");
      return false;
    } catch (e) {
      // Best-effort: if subscription check fails, guide user to pricing.
      trackEvent("deploy_blocked", {
        reason: "eligibility_network_error",
        channel: selectedChannel || "unknown",
        model_requested: selectedModel || null,
        subscription_tier: user?.subscriptionTier || null,
      });
      router.push("/pricing?source=deploy");
      return false;
    }
  };

  useEffect(() => {
    if (status !== "checking" || !deploymentId) return;

    const startTime = Date.now();
    const MAX_POLLING_TIME = (() => {
      const raw = process.env.NEXT_PUBLIC_DEPLOY_POLL_TIMEOUT_MS;
      const parsed = raw ? Number(raw) : Number.NaN;
      // Default to 10 minutes (first deploy can include pulling a large image).
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 10 * 60 * 1000;
    })();
    const timeoutMinutes = Math.max(1, Math.round(MAX_POLLING_TIME / 60_000));

    const interval = window.setInterval(async () => {
      try {
        // Check timeout
        if (Date.now() - startTime > MAX_POLLING_TIME) {
          setStatus("failed");
          setError(
            `Deployment timeout after ${timeoutMinutes} minutes. Please try again.`
          );
          window.clearInterval(interval);
          return;
        }

        const response = await fetch(`/api/deploy/${deploymentId}`);
        const result = await response.json().catch(() => ({}));

        if (!response.ok || result?.code === -1) {
          const rawMessage =
            result?.message || result?.error || "Failed to fetch deployment status";
          const errorCode =
            result?.error_code ||
            result?.errorCode ||
            result?.data?.error_code ||
            result?.data?.errorCode ||
            null;
          const transientAction = handleTransientPollFailure({
            rawMessage,
            errorCode,
            httpStatus: response.status,
          });
          if (transientAction === "retrying") {
            return;
          }
          if (transientAction === "failed") {
            window.clearInterval(interval);
            return;
          }
          handleDeployFailure({
            rawMessage,
            errorCode,
            source: "deploy_poll",
          });
          window.clearInterval(interval);
          return;
        }

        const data = result.data || result;
        pollTransientFailureCountRef.current = 0;
        const latestRequestedModel =
          data.requested_model ?? data.requestedModel ?? null;
        const latestResolvedModel =
          data.resolved_model ?? data.resolvedModel ?? null;

        if (latestRequestedModel) {
          setRequestedModel(latestRequestedModel);
        }
        if (latestResolvedModel) {
          setResolvedModel(latestResolvedModel);
        }

        if (data.status === "running") {
          if (!deploySuccessSentRef.current) {
            deploySuccessSentRef.current = true;
            const durationMs =
              typeof deployStartedAtRef.current === "number"
                ? Date.now() - deployStartedAtRef.current
                : undefined;
            const ctx = deployContextRef.current;
            trackEvent("deploy_succeeded", {
              channel: ctx?.channel || selectedChannel || "unknown",
              model_requested: latestRequestedModel ?? ctx?.modelRequested ?? null,
              model_resolved: latestResolvedModel ?? null,
              subscription_tier: ctx?.subscriptionTier || user?.subscriptionTier || null,
              duration_ms: typeof durationMs === "number" ? durationMs : undefined,
            });
          }
          setStatus("running");
          setError(null);
          window.clearInterval(interval);
        }
        if (data.status === "failed") {
          handleDeployFailure({
            rawMessage: data.error_message || data.errorMessage || null,
            errorCode: data.error_code || data.errorCode || null,
            source: "deploy_poll",
          });
          window.clearInterval(interval);
        }
      } catch (err) {
        const transientAction = handleTransientPollFailure({
          rawMessage: err instanceof Error ? err.message : null,
        });
        if (transientAction === "retrying") {
          return;
        }
        if (transientAction === "failed") {
          window.clearInterval(interval);
          return;
        }
        handleDeployFailure({
          rawMessage: err instanceof Error ? err.message : null,
          source: "deploy_poll",
        });
        window.clearInterval(interval);
      }
    }, 2000);

    return () => window.clearInterval(interval);
  }, [deploymentId, status]);

  const handleChannelSelect = (channelId: string) => {
    if (channelId === "telegram") {
      setShowTelegramGuide(true);
    }
    setSelectedChannel(channelId);
  };

  const handleSaveTelegramToken = () => {
    if (!guideToken.trim()) {
      setError("Please enter a bot token.");
      return;
    }
    setToken(guideToken);
    setShowTelegramGuide(false);
    setError(null);
  };

  const handleDeploy = async () => {
    trackEvent("deploy_clicked", {
      channel: selectedChannel || "unknown",
      model_requested: selectedModel || null,
      subscription_tier: user?.subscriptionTier || null,
    });

    const okToDeploy = await ensureDeployEligibility();
    if (!okToDeploy) {
      return;
    }

    if (!selectedChannel) {
      setError("Please select a channel before deploying.");
      return;
    }
    if (selectedChannel === "telegram" && !token.trim()) {
      setError("Please enter your Telegram bot token.");
      setShowTelegramGuide(true);
      return;
    }

    hasRedirectedToPricingRef.current = false;
    pollTransientFailureCountRef.current = 0;
    setStatus("checking");
    setError(null);
    setRequestedModel(selectedModel || null);
    setResolvedModel(null);
    setDeploymentId(null);

    try {
      deployStartedAtRef.current = Date.now();
      deploySuccessSentRef.current = false;
      deployFailureSentRef.current = false;
      deployContextRef.current = {
        channel: selectedChannel || "unknown",
        modelRequested: selectedModel || null,
        subscriptionTier: user?.subscriptionTier || null,
      };

      trackEvent("deploy_started", {
        channel: deployContextRef.current.channel,
        model_requested: deployContextRef.current.modelRequested,
        subscription_tier: deployContextRef.current.subscriptionTier,
      });

      const telegramToken = token.trim();

      const response = await fetch("/api/deploy", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          telegram_token: telegramToken,
          ...(selectedModel ? { model: selectedModel } : {}),
        }),
      });

      const payload = await response.json().catch(() => ({}));
      const rawError =
        payload?.message || payload?.error || "Failed to start deployment";
      const errorCode =
        payload?.error_code || payload?.errorCode || payload?.data?.error_code || payload?.data?.errorCode || null;

      if (!response.ok || payload?.code === -1) {
        handleDeployFailure({
          rawMessage: rawError,
          errorCode,
          source: "deploy_create",
        });
        return;
      }

      const deploymentData = payload.data || payload;
      const initialRequestedModel =
        deploymentData.requested_model ??
        deploymentData.requestedModel ??
        selectedModel ??
        null;
      const initialResolvedModel =
        deploymentData.resolved_model ?? deploymentData.resolvedModel ?? null;

      if (!deploymentData.deployment_id) {
        throw new Error("Invalid response: missing deployment_id");
      }

      setRequestedModel(initialRequestedModel);
      setResolvedModel(initialResolvedModel);
      setDeploymentId(deploymentData.deployment_id);
    } catch (err) {
      console.error("Deploy error:", err);
      handleDeployFailure({
        rawMessage: err instanceof Error ? err.message : "Failed to start deployment",
        source: "deploy_create",
      });
    }
  };

  return (
    <div className="relative">
      <div className="mx-auto max-w-6xl px-6 pb-24">
        <section className="pt-20 text-center md:pt-28">
          <div className="mx-auto max-w-3xl space-y-4">
            <h1 className="text-5xl font-semibold leading-tight tracking-tight md:text-6xl">
              SimpleClaw - One-click OpenClaw Deployment
            </h1>
            <p className="text-base text-muted-foreground md:text-lg">
              Deploy OpenClaw in under 1 minute. No servers or DevOps. Pick a
              model, connect a channel, and launch.
            </p>
          </div>
        </section>

        <section className="mt-14">
          <div className="rounded-3xl border border-border/70 bg-card/95 px-6 py-8 shadow-sm">
            <div className="space-y-10">
              <div>
                <h2 className="text-base font-semibold text-foreground/80">
                  Choose a default model for your OpenClaw
                </h2>
                <div className="mt-6 grid gap-4 md:grid-cols-3">
                  {MODELS.map((model) => (
                    <button
                      key={model.id}
                      type="button"
                      disabled={!model.available}
                      onClick={() => model.available && setSelectedModel(model.id)}
                      className={cn(
                        "group relative flex flex-col items-start gap-4 rounded-2xl border border-border/70 bg-card/90 p-5 text-left shadow-sm transition",
                        model.available
                          ? "hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
                          : "cursor-not-allowed opacity-60",
                        selectedModel === model.id && model.available
                          ? "border-primary/60 bg-primary/5"
                          : ""
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center size-10">
                          <img
                            src={model.icon}
                            alt={`${model.name} icon`}
                            className={cn(
                              "h-8 w-8 object-contain",
                              model.available ? "" : "opacity-70 grayscale"
                            )}
                            loading="lazy"
                          />
                        </div>
                        <div>
                          <p className="text-base font-semibold">{model.name}</p>
                          <span className="text-xs text-muted-foreground">
                            {model.subtitle}
                          </span>
                        </div>
                      </div>
                      {selectedModel === model.id && model.available && (
                        <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs text-primary">
                          Selected
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-base font-semibold text-foreground/80">
                  Choose where OpenClaw should send messages
                </h2>
                <div className="mt-6 grid gap-4 md:grid-cols-3">
                  {CHANNELS.map((channel) => (
                    <button
                      key={channel.id}
                      type="button"
                      disabled={!channel.available}
                      onClick={() =>
                        channel.available && handleChannelSelect(channel.id)
                      }
                      className={cn(
                        "group relative flex flex-col items-start gap-4 rounded-2xl border border-border/70 bg-card/90 p-5 text-left shadow-sm transition",
                        channel.available
                          ? "hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
                          : "cursor-not-allowed opacity-60",
                        selectedChannel === channel.id && channel.available
                          ? "border-primary/60 bg-primary/5"
                          : ""
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center size-10">
                          <img
                            src={channel.icon}
                            alt={`${channel.name} icon`}
                            className={cn(
                              "h-8 w-8 object-contain",
                              channel.available ? "" : "opacity-70 grayscale"
                            )}
                            loading="lazy"
                          />
                        </div>
                        <div>
                          <p className="text-base font-semibold">
                            {channel.name}
                          </p>
                          <span className="text-xs text-muted-foreground">
                            {channel.subtitle}
                          </span>
                        </div>
                      </div>
                      {selectedChannel === channel.id && channel.available && (
                        <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs text-primary">
                          Selected
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-border/60 pt-8">
                <div>
                  {/* Deploy Section */}
                  <div className="space-y-4">
                    {/* Deploy Button - Sign in to deploy or Deploy */}
                    <Button
                      onClick={isSignedIn ? handleDeploy : () => setShowSignModal(true)}
                      disabled={status === "checking"}
                      className={cn(
                        "w-full md:w-auto min-w-[200px] h-12 px-8 rounded-xl font-semibold text-sm transition-all duration-300",
                        "bg-gradient-to-r from-muted via-muted/90 to-muted hover:from-muted/80 hover:via-muted/70 hover:to-muted/80",
                        "border border-border/50 hover:border-border",
                        "shadow-[0_2px_8px_-2px_rgba(0,0,0,0.2)] hover:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.3)]",
                        "text-foreground hover:text-foreground/90",
                        "disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:from-muted",
                        "flex items-center justify-center gap-2"
                      )}
                    >
                      {status === "checking" ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Deploying...</span>
                        </>
                      ) : isSignedIn ? (
                        <>
                          <Zap className="h-4 w-4 fill-current" />
                          <span>Deploy</span>
                        </>
                      ) : (
                        <>
                          <Zap className="h-4 w-4 fill-current" />
                          <span>Sign in to deploy with SimpleClaw</span>
                        </>
                      )}
                    </Button>

                    {!isSignedIn && (
                      <p className="text-sm text-muted-foreground">
                        Sign in to deploy OpenClaw with SimpleClaw and connect
                        your channels.
                      </p>
                    )}

                    {/* Status Messages - Only show when signed in */}
                    {isSignedIn && (
                      <>
                        {status === "running" && (
                          <p className="text-sm text-green-500 font-medium">
                            ✓ Deployment successful! OpenClaw is now running.
                          </p>
                        )}
                        {status === "checking" && error && (
                          <p className="text-sm text-yellow-500">
                            {error}
                          </p>
                        )}
                        {status === "failed" && error && (
                          <p className="text-sm text-red-500">
                            ✗ {error}
                          </p>
                        )}
                        {status === "idle" && error && (
                          <p className="text-sm text-red-500">
                            ✗ {error}
                          </p>
                        )}

                        {hasModelMismatch && (
                          <p className="text-sm text-yellow-500">
                            ⚠ Model substitution detected. Try redeploying or switch
                            to another region/provider.
                          </p>
                        )}

                        {/* Hint Text */}
                        <p className="text-sm text-muted-foreground">
                          <span className="text-foreground font-medium">Connect Telegram to continue.</span>{" "}
                          You can add other channels to the same account later.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Connect Telegram Dialog */}
      <Dialog open={showTelegramGuide} onOpenChange={setShowTelegramGuide}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a]/95 border-border/70 rounded-3xl p-0">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Left Column - Guide Content */}
            <div className="p-8 space-y-6">
              <DialogHeader className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="grid size-10 place-items-center rounded-xl bg-gradient-to-br from-primary/20 via-primary/10 to-transparent ring-1 ring-primary/20">
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/82/Telegram_logo.svg/960px-Telegram_logo.svg.png"
                      alt="Telegram icon"
                      className="h-6 w-6 object-contain"
                    />
                  </div>
                  <DialogTitle className="text-xl font-semibold text-foreground">
                    Connect Telegram
                  </DialogTitle>
                </div>
              </DialogHeader>

              <div className="space-y-4">
                <h3 className="text-base font-semibold text-foreground/90">
                  How to get your bot token?
                </h3>
                
                <ol className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex gap-3">
                    <span className="text-primary font-semibold">1.</span>
                    <span>
                      Open Telegram and go to{" "}
                      <a
                        href="https://t.me/BotFather"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        @BotFather
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-semibold">2.</span>
                    <span>
                      Start a chat and type{" "}
                      <code className="font-mono bg-muted px-1.5 py-0.5 rounded text-foreground/80">
                        /newbot
                      </code>
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-semibold">3.</span>
                    <span>Follow the prompts to name your bot and choose a username.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-semibold">4.</span>
                    <span>
                      BotFather will send you a message with your bot token. Copy the
                      entire token (it looks like a long string of numbers and letters).
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-semibold">5.</span>
                    <span>Paste the token in the field below and click Save & Connect.</span>
                  </li>
                </ol>
              </div>

              <div className="space-y-3 pt-4 border-t border-border/60">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Enter bot token
                </label>
                <input
                  value={guideToken}
                  onChange={(e) => setGuideToken(e.target.value)}
                  placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                  className="w-full h-11 rounded-md border border-border bg-background px-4 text-sm outline-none focus:ring-2 focus:ring-primary/40 text-foreground placeholder:text-muted-foreground"
                />
                <Button
                  onClick={handleSaveTelegramToken}
                  disabled={!guideToken.trim()}
                  className="w-full h-11 rounded-md text-sm font-semibold transition shadow-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
                >
                  <span className="flex items-center justify-center gap-2">
                    Save & Connect
                    <Check className="h-4 w-4" />
                  </span>
                </Button>
                {error && (
                  <p className="text-xs text-red-500">{error}</p>
                )}
              </div>
            </div>

            {/* Right Column - Phone Screenshot */}
            <div className="hidden md:flex items-center justify-center bg-gradient-to-br from-primary/5 via-transparent to-transparent p-8">
              <div className="relative">
                {/* Phone Frame */}
                <div className="w-[280px] h-[560px] rounded-[40px] bg-[#1a1a2e] border-4 border-[#2a2a3e] shadow-2xl overflow-hidden">
                  {/* Screen Content */}
                  <div className="h-full flex flex-col">
                    {/* Status Bar */}
                    <div className="h-8 bg-[#1a1a2e] flex items-center justify-between px-6 pt-2">
                      <span className="text-[10px] text-white/80">9:41</span>
                      <div className="flex gap-1">
                        <div className="w-4 h-2.5 bg-white/80 rounded-sm" />
                        <div className="w-3 h-2.5 bg-white/80 rounded-sm" />
                      </div>
                    </div>
                    
                    {/* Chat Header */}
                    <div className="bg-[#1a1a2e] px-4 py-3 flex items-center gap-3 border-b border-white/10">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold">
                        B
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">BotFather</p>
                        <p className="text-[10px] text-white/60">bot</p>
                      </div>
                    </div>

                    {/* Chat Messages */}
                    <div className="flex-1 bg-[#0d0d1a] p-4 space-y-3 overflow-hidden">
                      {/* BotFather Message */}
                      <div className="flex justify-start">
                        <div className="max-w-[85%] bg-[#2a2a3e] rounded-2xl rounded-tl-sm px-4 py-2.5">
                          <p className="text-xs text-white/90">
                            I can help you create and manage Telegram bots. What would you like to do?
                          </p>
                          <p className="text-[10px] text-white/50 mt-1.5">9:35 AM</p>
                        </div>
                      </div>

                      {/* Commands */}
                      <div className="flex justify-start">
                        <div className="max-w-[90%] space-y-1">
                          <div className="bg-[#1a1a2e] rounded-lg px-3 py-2 border border-white/10">
                            <p className="text-xs text-blue-400 font-medium">/newbot</p>
                            <p className="text-[10px] text-white/60">create a new bot</p>
                          </div>
                          <div className="bg-[#1a1a2e] rounded-lg px-3 py-2 border border-white/10 opacity-60">
                            <p className="text-xs text-blue-400 font-medium">/mybots</p>
                            <p className="text-[10px] text-white/60">edit your bots</p>
                          </div>
                        </div>
                      </div>

                      {/* User Message */}
                      <div className="flex justify-end">
                        <div className="max-w-[70%] bg-[#3b82f6] rounded-2xl rounded-tr-sm px-4 py-2.5">
                          <p className="text-xs text-white">/newbot</p>
                          <p className="text-[10px] text-white/70 mt-1.5 text-right">9:36 AM</p>
                        </div>
                      </div>

                      {/* BotFather Response */}
                      <div className="flex justify-start">
                        <div className="max-w-[85%] bg-[#2a2a3e] rounded-2xl rounded-tl-sm px-4 py-2.5">
                          <p className="text-xs text-white/90">
                            Alright, a new bot. How are we going to call it? Please choose a name for your bot.
                          </p>
                          <p className="text-[10px] text-white/50 mt-1.5">9:36 AM</p>
                        </div>
                      </div>
                    </div>

                    {/* Input Area */}
                    <div className="bg-[#1a1a2e] px-4 py-3 border-t border-white/10">
                      <div className="flex items-center gap-2 bg-[#0d0d1a] rounded-full px-4 py-2">
                        <div className="w-6 h-6 rounded-full bg-white/10" />
                        <span className="text-sm text-white/40 flex-1">Message</span>
                        <div className="w-6 h-6 rounded-full bg-[#3b82f6] flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-primary/20 rounded-full blur-2xl" />
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue-500/20 rounded-full blur-xl" />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
